import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/subscription/current`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const hasFeature = useCallback((feature) => {
    if (!subscription) return false;
    return subscription.features?.[feature] === true;
  }, [subscription]);

  const getUsage = useCallback((meter) => {
    if (!subscription) return { used: 0, limit: 0, percentage: 0 };
    const used = subscription.usage?.[meter] || 0;
    const limit = subscription.limits?.[meter] || 0;
    const percentage = limit === -1 ? 0 : limit === 0 ? (used > 0 ? 100 : 0) : Math.min(Math.round((used / limit) * 100), 100);
    return { used, limit, percentage };
  }, [subscription]);

  const isAtLimit = useCallback((meter) => {
    const { used, limit } = getUsage(meter);
    if (limit === -1) return false;
    return used >= limit;
  }, [getUsage]);

  const value = {
    subscription,
    loading,
    hasFeature,
    getUsage,
    isAtLimit,
    refreshSubscription: fetchSubscription,
    plan: subscription?.plan || 'free',
    planName: subscription?.plan_name || 'Free',
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
